import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"

/**
 * LoadingState — consistent loading skeleton/spinner across all pages.
 *
 * Variants:
 *   - page: full page skeleton with header + content blocks
 *   - card: single card skeleton (default)
 *   - list: avatar + text rows
 *   - spinner: centered spinning indicator with optional message
 *
 * Applies Troopn design-system tokens: brass-accent spinner color,
 * parchment-tinted skeletons.
 */

const loadingStateVariants = cva("", {
  variants: {
    variant: {
      page: "",
      card: "",
      list: "",
      spinner: "",
    },
  },
  defaultVariants: {
    variant: "card",
  },
})

interface LoadingStateProps extends VariantProps<typeof loadingStateVariants> {
  className?: string
  /** Number of skeleton items to render for list/card variants */
  count?: number
  /** Optional message shown below the spinner variant */
  message?: string
}

function LoadingStateInner({
  variant = "card",
  count,
  message,
  className,
}: LoadingStateProps) {
  if (variant === "spinner") {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-3 py-12",
          className,
        )}
        role="status"
        aria-label={message ?? "Loading"}
      >
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--accent-app))]" />
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
      </div>
    )
  }

  if (variant === "page") {
    return (
      <div className={cn("space-y-6 p-6", className)} role="status" aria-label="Loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {Array.from({ length: count ?? 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (variant === "list") {
    const itemCount = count ?? 4
    return (
      <div className={cn("space-y-3", className)} role="status" aria-label="Loading">
        {Array.from({ length: itemCount }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // card (default)
  const cardCount = count ?? 1
  return (
    <div className={cn("space-y-4", className)} role="status" aria-label="Loading">
      {Array.from({ length: cardCount }).map((_, i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      ))}
    </div>
  )
}

const LoadingState = React.memo(LoadingStateInner)

export { LoadingState, loadingStateVariants }
export type { LoadingStateProps }
